import { ReactElement, useEffect, useRef, useState } from "react";
import { Dialog } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import party from 'party-js';
import { partial } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/redux-store";
import { SurveyStatus } from "../../../redux/survey/survey.types";
import { setShownComplete } from "../../../redux/survey/survey-animation.slice";
import { useIsMobile } from "src/hooks/useMobile";

interface UseSurveyCompleteDialogHook {
  readonly mode: 'view' | string & NonNullable<unknown>;
  readonly surveyId?: string;
  readonly status?: SurveyStatus;
}

export const useSurveyCompleteDialogSetup = ({ mode, surveyId, status }: UseSurveyCompleteDialogHook): any => {
  const hasShownComplete = useSelector((state: RootState) => state.surveyAnimationState?.hasShownComplete[surveyId ?? ''] || false);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (!hasShownComplete && surveyId && mode === 'view' && status === SurveyStatus.Complete) {
      setShowDialog(true);
    }
  }, [hasShownComplete, surveyId, mode, status]);

  return {
    showDialog,
    setShowDialog,
  };
};

export interface SurveyCompleteDialogProps {
  readonly surveyId: string;
  readonly showDialog: boolean;
  readonly setShowDialog: (val: boolean) => void;
}

export const SurveyCompleteDialog = ({ surveyId, showDialog, setShowDialog }: SurveyCompleteDialogProps): ReactElement | null => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useIsMobile();
  const dispatch = useDispatch();

  useEffect(() => {
    if (showDialog && dialogRef.current) {
      party.confetti(dialogRef.current, { count: isMobile ? 300 : 800 });
      dispatch(setShownComplete({ surveyId }));
    }
  }, [showDialog]);

  if (!showDialog) {
    return null;
  }

  return (
    <Dialog>
      <div
        className="absolute size-full bg-blue-500 opacity-90 z-50 flex flex-col align-middle justify-center gap-8 p-2"
        ref={dialogRef}
      >
        <p className="text-4xl sm:text-5xl text-white text-center">
          Good news, your
          <br />
          survey is complete!
        </p>
        <div className="flex justify-center">
          <Button
            onClick={partial(setShowDialog, false)}
            variant='black'
          >
            See survey results
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
